import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Download, Eye, Loader2, X } from 'lucide-react';
import { toast } from 'react-hot-toast';
import { ApiCallError, callFunction } from '@/lib/api';
import { labConsentAllowsShare } from '@/lib/lab/labConsent';
import type { LabObjectLink, LabReference } from '@/lib/lab/labTypes';
import { acceptedHttpUrl } from '@/lib/lab/safeHttpUrl';
import {
  attachmentViewKind,
  base64ToBlob,
  formatAttachmentBytes,
  normalizeObjectIdentifier,
} from '@/lib/referral/attachmentViewer';

type DownloadedObject = {
  file_name: string;
  mime_type: string;
  size: number;
  base64: string;
  object_identifier?: string | null;
  original_name?: string | null;
};

export function LabReferenceList({
  refs,
  clinicId,
  reportId,
  links,
  consentGiven,
}: {
  refs: LabReference[] | undefined;
  clinicId: string;
  reportId: string;
  links: LabObjectLink[];
  consentGiven: boolean | null | undefined;
}) {
  if (!refs || refs.length === 0) return null;
  return (
    <ul className="mt-s2 space-y-1">
      {refs.map((ref, i) => (
        <li key={`${ref.objectIdentifier ?? ref.url ?? i}`}>
          <LabReferenceItem
            refItem={ref}
            clinicId={clinicId}
            reportId={reportId}
            links={links}
            consentGiven={consentGiven}
          />
        </li>
      ))}
    </ul>
  );
}

function LabReferenceItem({
  refItem,
  clinicId,
  reportId,
  links,
  consentGiven,
}: {
  refItem: LabReference;
  clinicId: string;
  reportId: string;
  links: LabObjectLink[];
  consentGiven: boolean | null | undefined;
}) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const href = acceptedHttpUrl(refItem.url);
  const originalName = refItem.refDescription?.trim() || null;
  if (href) {
    return (
      <a className="lab-link" href={href} target="_blank" rel="noopener noreferrer">
        {originalName || href}
      </a>
    );
  }
  const key = normalizeObjectIdentifier(refItem.objectIdentifier);
  const link = links.find((l) => l.object_key === key);
  const received = Boolean(link?.xbin_exchange_id);
  const objectId = refItem.objectIdentifier?.trim() || null;
  const label = originalName || objectId || t('patients.lab.object');
  const meta = [
    objectId && objectId !== originalName ? `${t('patients.lab.object_identifier')}: ${objectId}` : null,
    refItem.objectCode ? `${t('patients.lab.object_code')}: ${refItem.objectCode}` : null,
    refItem.objectExtensionCode,
    refItem.originalObjectSize ? `${refItem.originalObjectSize} B` : null,
  ]
    .filter(Boolean)
    .join(' · ');

  if (!key) {
    return (
      <span>
        {label}
        {meta ? ` (${meta})` : ''}
      </span>
    );
  }
  if (!received) {
    return (
      <span>
        {label}
        {meta ? ` · ${meta}` : ''}
        <span className="ml-1 text-ds-muted">{t('patients.lab.object_not_received')}</span>
      </span>
    );
  }
  return (
    <span>
      <button type="button" className="lab-link inline-flex items-center gap-1" onClick={() => setOpen(true)}>
        <Eye className="h-4 w-4" />
        {t('patients.lab.object_open')}
      </button>
      <span className="ml-1 text-ds-muted">
        {label}
        {meta ? ` · ${meta}` : ''}
      </span>
      {open ? (
        <LabObjectViewer
          clinicId={clinicId}
          reportId={reportId}
          objectIdentifier={refItem.objectIdentifier ?? key}
          originalName={originalName}
          consentGiven={consentGiven}
          onClose={() => setOpen(false)}
        />
      ) : null}
    </span>
  );
}

function LabObjectViewer({
  clinicId,
  reportId,
  objectIdentifier,
  originalName,
  consentGiven,
  onClose,
}: {
  clinicId: string;
  reportId: string;
  objectIdentifier: string;
  originalName: string | null;
  consentGiven: boolean | null | undefined;
  onClose: () => void;
}) {
  const { t, i18n } = useTranslation();
  const [file, setFile] = useState<DownloadedObject | null>(null);
  const [url, setUrl] = useState<string | null>(null);
  const [failed, setFailed] = useState(false);
  const [undecodable, setUndecodable] = useState(false);
  const canExport = labConsentAllowsShare(consentGiven);

  useEffect(() => {
    let cancelled = false;
    let objectUrl: string | null = null;
    callFunction<
      { action: 'object-download'; clinic_id: string; report_id: string; object_identifier: string },
      DownloadedObject
    >('communicate-lab-import', {
      action: 'object-download',
      clinic_id: clinicId,
      report_id: reportId,
      object_identifier: objectIdentifier,
    })
      .then((f) => {
        if (cancelled) return;
        objectUrl = URL.createObjectURL(base64ToBlob(f.base64, f.mime_type));
        setFile(f);
        setUrl(objectUrl);
      })
      .catch((err) => {
        if (cancelled) return;
        setFailed(true);
        toast.error(err instanceof ApiCallError ? err.message : t('patients.lab.object_load_failed'));
      });
    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [clinicId, reportId, objectIdentifier, t]);

  const kind = attachmentViewKind(file?.mime_type);
  const locale = i18n.language === 'en' ? 'en-US' : 'da-DK';
  const shownName = file?.original_name || originalName || file?.file_name || t('patients.lab.object_open');
  const shownId = file?.object_identifier || objectIdentifier;
  const downloadName = file?.file_name || originalName || objectIdentifier;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-ds-scrim p-4" role="dialog" aria-modal="true">
      <div className="flex h-full max-h-[90vh] w-full max-w-4xl flex-col rounded-xl bg-ds-card shadow-pop">
        <div className="flex items-center justify-between gap-3 border-b border-ds-line px-4 py-3">
          <div className="min-w-0">
            <div className="truncate text-sm font-semibold text-ds-text">{shownName}</div>
            <div className="text-xs text-ds-muted">
              {t('patients.lab.object_identifier')}: {shownId}
              {file ? ` · ${file.mime_type} · ${formatAttachmentBytes(file.size, locale)}` : ''}
            </div>
          </div>
          <div className="flex items-center gap-2">
            {file && url && canExport ? (
              <a
                href={url}
                download={downloadName}
                className="inline-flex items-center gap-1 rounded-md px-2 py-1 text-sm font-medium text-ds-teal hover:bg-ds-teal-tint"
              >
                <Download className="h-4 w-4" />
                {t('patients.lab.object_download')}
              </a>
            ) : null}
            {file && !canExport ? (
              <span className="text-xs text-ds-muted">{t('patients.lab.object_export_blocked')}</span>
            ) : null}
            <button
              type="button"
              onClick={onClose}
              className="rounded-md p-1 text-ds-muted hover:bg-ds-row-hover"
              aria-label={t('common.close', 'Luk')}
            >
              <X className="h-5 w-5" />
            </button>
          </div>
        </div>
        <div className="min-h-0 flex-1 overflow-auto bg-ds-sunk p-3">
          {!file && !failed ? (
            <div className="flex h-full items-center justify-center">
              <Loader2 className="h-6 w-6 animate-spin text-ds-muted" />
            </div>
          ) : null}
          {failed ? <div className="p-4 text-sm text-ds-red-ink">{t('patients.lab.object_load_failed')}</div> : null}
          {file && url && kind === 'image' && !undecodable ? (
            <img
              src={url}
              alt={shownName}
              className="mx-auto max-h-full max-w-full object-contain"
              onError={() => setUndecodable(true)}
            />
          ) : null}
          {file && url && kind === 'image' && undecodable ? (
            <div role="alert" className="p-4 text-sm text-ds-amber-ink">
              {t('patients.lab.object_undecodable')}
            </div>
          ) : null}
          {file && url && (kind === 'pdf' || kind === 'text') ? (
            <iframe src={url} title={shownName} className="h-full min-h-[60vh] w-full rounded bg-ds-card" />
          ) : null}
          {file && url && kind === 'download' ? (
            <div className="p-4 text-sm text-ds-text">{t('patients.lab.object_no_preview')}</div>
          ) : null}
        </div>
      </div>
    </div>
  );
}
